import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    Box,
    TextField
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    EmojiEvents as EmojiEventsIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import { authService } from '../../services/authService';
import { activateAllUsers } from '../../scripts/activateAllUsers';

interface UserData {
    id: string;
    displayName: string;
    email: string;
    phone: string;
    createdAt: string;
    updatedAt?: string;
    isActive: boolean;
    points: number;
}

const UsersAdmin: React.FC = () => {
    const [users, setUsers] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(true);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        userId: string | null;
        isActive: boolean;
        userName: string;
    }>({
        open: false,
        userId: null,
        isActive: false,
        userName: ''
    });
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [pointsDialog, setPointsDialog] = useState<{
        open: boolean;
        userId: string | null;
        userName: string;
        currentPoints: number;
        pointsToAdd: string;
        reason: string;
    }>({
        open: false,
        userId: null,
        userName: '',
        currentPoints: 0,
        pointsToAdd: '',
        reason: ''
    });
    
    // Yeni state: Puan düzenleme dialogu için
    const [editPointsDialog, setEditPointsDialog] = useState<{
        open: boolean;
        userId: string | null;
        userName: string;
        currentPoints: number;
        newPoints: string;
        reason: string;
    }>({
        open: false,
        userId: null,
        userName: '',
        currentPoints: 0,
        newPoints: '',
        reason: ''
    });

    const loadUsers = async () => {
        try {
            setLoading(true);
            const usersData = await authService.getAllUsers();
            setUsers(usersData as UserData[]);
        } catch (error) {
            console.error('Error loading users:', error);
            showSnackbar('Kullanıcılar yüklenirken bir hata oluştu', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const handleStatusChange = async (userId: string, displayName: string, newStatus: boolean) => {
        setConfirmDialog({
            open: true,
            userId,
            isActive: newStatus,
            userName: displayName
        });
    };

    const handleConfirmStatusChange = async () => {
        if (!confirmDialog.userId) return;

        try {
            await authService.updateUserStatus(confirmDialog.userId, confirmDialog.isActive);
            showSnackbar('Kullanıcı durumu güncellendi', 'success');
            loadUsers();
        } catch (error) {
            console.error('Error updating user status:', error);
            showSnackbar('Kullanıcı durumu güncellenirken bir hata oluştu', 'error');
        } finally {
            setConfirmDialog(prev => ({ ...prev, open: false }));
        }
    };

    const handleActivateAllUsers = async () => {
        try {
            setLoading(true);
            const updatedCount = await activateAllUsers();
            if (updatedCount > 0) {
                showSnackbar(`${updatedCount} kullanıcı başarıyla aktifleştirildi`, 'success');
            } else {
                showSnackbar('Tüm kullanıcılar zaten aktif', 'success');
            }
            loadUsers(); // Listeyi yenile
        } catch (error) {
            console.error('Error activating all users:', error);
            showSnackbar('Kullanıcılar aktifleştirilirken bir hata oluştu', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAddPoints = (userId: string, userName: string, currentPoints: number) => {
        setPointsDialog({
            open: true,
            userId,
            userName,
            currentPoints,
            pointsToAdd: '',
            reason: ''
        });
    };

    // Yeni fonksiyon: Puan düzenleme dialogunu açma
    const handleEditPoints = (userId: string, userName: string, currentPoints: number) => {
        setEditPointsDialog({
            open: true,
            userId,
            userName,
            currentPoints,
            newPoints: currentPoints.toString(),
            reason: 'Puan düzenleme'
        });
    };

    const handleConfirmAddPoints = async () => {
        if (!pointsDialog.userId || !pointsDialog.pointsToAdd || !pointsDialog.reason) return;

        const points = parseInt(pointsDialog.pointsToAdd);
        if (isNaN(points) || points <= 0) {
            showSnackbar('Geçerli bir puan değeri giriniz', 'error');
            return;
        }

        try {
            // Önce puan ekle
            await authService.addPoints(pointsDialog.userId, points);
            showSnackbar('Puanlar başarıyla eklendi', 'success');
            await loadUsers(); // Kullanıcı listesini yenile
            setPointsDialog(prev => ({ ...prev, open: false }));
        } catch (error) {
            console.error('Error in handleConfirmAddPoints:', error);
            showSnackbar('Puan eklenirken bir hata oluştu', 'error');
        }
    };

    // Yeni fonksiyon: Puan düzenleme onaylama
    const handleConfirmEditPoints = async () => {
        if (!editPointsDialog.userId || !editPointsDialog.newPoints || !editPointsDialog.reason) return;

        const newPoints = parseInt(editPointsDialog.newPoints);
        if (isNaN(newPoints) || newPoints < 0) {
            showSnackbar('Geçerli bir puan değeri giriniz', 'error');
            return;
        }

        try {
            const pointsDiff = newPoints - editPointsDialog.currentPoints;
            await authService.setPoints(editPointsDialog.userId, newPoints);
            showSnackbar('Puanlar başarıyla güncellendi', 'success');
            await loadUsers(); // Kullanıcı listesini yenile
            setEditPointsDialog(prev => ({ ...prev, open: false }));
        } catch (error) {
            console.error('Error in handleConfirmEditPoints:', error);
            showSnackbar('Puanlar güncellenirken bir hata oluştu', 'error');
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const columns: GridColDef[] = [
        { field: 'displayName', headerName: 'İsim', width: 200 },
        { field: 'email', headerName: 'E-posta', width: 250 },
        { field: 'phone', headerName: 'Telefon', width: 150 },
        {
            field: 'points',
            headerName: 'Puanlar',
            width: 130,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmojiEventsIcon color="primary" fontSize="small" />
                    <Typography>{params.value || 0}</Typography>
                </Box>
            )
        },
        {
            field: 'isActive',
            headerName: 'Durum',
            width: 130,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Aktif' : 'Pasif'}
                    color={params.value ? 'success' : 'error'}
                    size="small"
                />
            )
        },
        {
            field: 'createdAt',
            headerName: 'Kayıt Tarihi',
            width: 200,
            renderCell: (params) => {
                const date = new Date(params.value as string);
                return date.toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
        },
        {
            field: 'actions',
            headerName: 'İşlemler',
            width: 200,
            renderCell: (params) => {
                const isActive = params.row.isActive;
                const isAdmin = params.row.email === 'admin@therosetarot.com';

                if (isAdmin) return null;

                return (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={isActive ? 'Pasife Al' : 'Aktife Al'}>
                            <IconButton
                                onClick={() => handleStatusChange(params.row.id, params.row.displayName, !isActive)}
                                color={isActive ? 'error' : 'success'}
                            >
                                {isActive ? <BlockIcon /> : <CheckCircleIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Puan Ekle">
                            <IconButton
                                onClick={() => handleAddPoints(params.row.id, params.row.displayName, params.row.points || 0)}
                                color="primary"
                            >
                                <EmojiEventsIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Puanları Düzenle">
                            <IconButton
                                onClick={() => handleEditPoints(params.row.id, params.row.displayName, params.row.points || 0)}
                                color="primary"
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            }
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Kullanıcı Yönetimi
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                        Normal kullanıcıların listesi ve yönetimi
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleActivateAllUsers}
                    disabled={loading}
                >
                    Tüm Hesapları Aktifleştir
                </Button>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <DataGrid
                    rows={users}
                    columns={columns}
                    loading={loading}
                    autoHeight
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                            },
                        },
                    }}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            >
                <DialogTitle>Kullanıcı Durumu Değiştirme</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.userName} isimli kullanıcının hesabını
                        {' '}
                        {confirmDialog.isActive ? 'aktifleştirmek' : 'pasifleştirmek'}
                        {' '}
                        istediğinizden emin misiniz?
                        {!confirmDialog.isActive && (
                            <>
                                <br /><br />
                                <strong>Not:</strong> Pasif hesaplar sisteme giriş yapamayacaktır.
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>
                        İptal
                    </Button>
                    <Button
                        onClick={handleConfirmStatusChange}
                        color={confirmDialog.isActive ? 'success' : 'error'}
                        variant="contained"
                    >
                        {confirmDialog.isActive ? 'Aktifleştir' : 'Pasifleştir'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={pointsDialog.open}
                onClose={() => setPointsDialog(prev => ({ ...prev, open: false }))}
            >
                <DialogTitle>Puan Ekle</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        <strong>{pointsDialog.userName}</strong> isimli kullanıcıya puan ekle
                        <br />
                        Mevcut Puanlar: <strong>{pointsDialog.currentPoints}</strong>
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Eklenecek Puan"
                        type="number"
                        fullWidth
                        value={pointsDialog.pointsToAdd}
                        onChange={(e) => setPointsDialog(prev => ({ ...prev, pointsToAdd: e.target.value }))}
                        inputProps={{ min: 1 }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Puan Ekleme Nedeni"
                        type="text"
                        fullWidth
                        required
                        value={pointsDialog.reason}
                        onChange={(e) => setPointsDialog(prev => ({ ...prev, reason: e.target.value }))}
                        placeholder="Puan ekleme nedenini belirtiniz (Örn: Özel promosyon, Hediye puan)"
                        helperText="Puan ekleme nedeni zorunludur"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPointsDialog(prev => ({ ...prev, open: false }))}>
                        İptal
                    </Button>
                    <Button
                        onClick={handleConfirmAddPoints}
                        color="primary"
                        variant="contained"
                        disabled={!pointsDialog.pointsToAdd || !pointsDialog.reason}
                    >
                        Puan Ekle
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={editPointsDialog.open}
                onClose={() => setEditPointsDialog(prev => ({ ...prev, open: false }))}
            >
                <DialogTitle>Puanları Düzenle</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        <strong>{editPointsDialog.userName}</strong> isimli kullanıcının puanlarını düzenle
                        <br />
                        Mevcut Puanlar: <strong>{editPointsDialog.currentPoints}</strong>
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Yeni Puan Değeri"
                        type="number"
                        fullWidth
                        value={editPointsDialog.newPoints}
                        onChange={(e) => setEditPointsDialog(prev => ({ ...prev, newPoints: e.target.value }))}
                        inputProps={{ min: 0 }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Düzenleme Nedeni"
                        type="text"
                        fullWidth
                        required
                        value={editPointsDialog.reason}
                        onChange={(e) => setEditPointsDialog(prev => ({ ...prev, reason: e.target.value.trim() }))}
                        placeholder="Puan düzenleme nedenini belirtiniz (Örn: Hatalı puan düzeltme, Manuel puan ayarlama)"
                        helperText="Puan düzenleme nedeni zorunludur"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditPointsDialog(prev => ({ ...prev, open: false }))}>
                        İptal
                    </Button>
                    <Button
                        onClick={handleConfirmEditPoints}
                        color="primary"
                        variant="contained"
                        disabled={!editPointsDialog.newPoints || !editPointsDialog.reason}
                    >
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default UsersAdmin; 